/**
 * Placement of a chat figure, read from the fence info string after the
 * language, e.g. ```mermaid center third. Mermaid itself has no layout
 * vocabulary, so this is the one place an author (usually the agent) can
 * say "centre this" or "put these three side by side".
 */

export type FigureAlign = "left" | "center" | "right";
export type FigureWidth = "full" | "half" | "third";

export type FigureView = "figure" | "source";

export interface FigureLayout {
  readonly align: FigureAlign;
  readonly width: FigureWidth;
  /** `source` shows the highlighted code first — for "give me the mermaid". */
  readonly view: FigureView;
}

export const DEFAULT_FIGURE_LAYOUT: FigureLayout = {
  align: "left",
  width: "full",
  view: "figure",
};

const VIEW_WORDS: Readonly<Record<string, FigureView>> = {
  figure: "figure",
  render: "figure",
  source: "source",
  code: "source",
  raw: "source",
};

const ALIGN_WORDS: Readonly<Record<string, FigureAlign>> = {
  left: "left",
  center: "center",
  centre: "center",
  middle: "center",
  right: "right",
};

const WIDTH_WORDS: Readonly<Record<string, FigureWidth>> = {
  full: "full",
  wide: "full",
  half: "half",
  "1/2": "half",
  third: "third",
  "1/3": "third",
};

/**
 * Accepts bare words (`center`, `third`) and `key=value` pairs
 * (`align=center`, `width=half`); anything else is left for other readers of
 * the info string, such as the `title=` attribute.
 */
export function parseFigureLayout(meta: string | undefined | null): FigureLayout {
  if (!meta) return DEFAULT_FIGURE_LAYOUT;
  let align = DEFAULT_FIGURE_LAYOUT.align;
  let width = DEFAULT_FIGURE_LAYOUT.width;
  let view = DEFAULT_FIGURE_LAYOUT.view;
  for (const token of meta.trim().split(/\s+/)) {
    const [key, value] = token.includes("=") ? token.split("=", 2) : [null, token];
    const word = (value ?? "").toLowerCase().replace(/^["']|["']$/g, "");
    if (key == null || key.toLowerCase() === "view") {
      const found = VIEW_WORDS[word];
      if (found) {
        view = found;
        continue;
      }
    }
    if (key == null || key.toLowerCase() === "align") {
      const found = ALIGN_WORDS[word];
      if (found) {
        align = found;
        if (key != null) continue;
      }
    }
    if (key == null || key.toLowerCase() === "width") {
      const found = WIDTH_WORDS[word];
      if (found) width = found;
    }
  }
  return { align, width, view };
}
